import React, { useReducer, useMemo } from 'react'
import { Button, Paragraph, FlowGrid, useOverlay } from '@fs/zion-ui'
import { useSlsDispatchContext } from '@fs/zion-sls'
import { useTranslation } from 'react-i18next'
import { useSelectedPerson } from '../../../../../shared/hooks/useSelectedPerson'
import { EssentialInformationCard } from '../../../../components/generic/essentialInformationCard'
import { RelationshipsCard } from '../../../../components/generic/relationshipsCard/RelationshipsCard'
import EventsCard from '../../../../components/generic/eventsCard/EventsCard'
import AdditionalFactsCard from '../../../../components/generic/additionalFactsCard/AdditionalFactsCard'
import TreeAttachmentCard from '../../../../components/generic/treeAttachmentCard/TreeAttachmentCard'
import { DeleteOverlay } from '../overlays'
import { useOverlayContext } from '../../../../../shared/providers/overlayContextProvider/OverlayContext'
import ShowChangeHistory from '../ui/ShowChangeHistory'
import { stepperEditViewInitialState, stepperReducer, STEPS } from '../../../../utils/stepperReducer'
import { useDisplayHighlights } from '../../../../hooks/useDisplayHighlights'
import useRecordOutlineHighlightsEnabled from '../../../../hooks/useRecordOutlineHighlightsEnabled'
import { useSharedBrowserStateContext } from '../../../../../shared/providers/sharedBrowserProvider/SharedBrowserContext'
import { useR2StateContext } from '../../../../providers/r2Provider/R2Context'
import { useCollectionContextProvider } from '../../../../providers/collectionProvider/CollectionProvider'
import { FIELDS } from '../../../../providers/collectionProvider/collectionConstants'

/**
 * PersonOverviewV2 - A comprehensive view component for displaying and managing a selected person's information
 * in the Record Image Processor (RIP) indexing interface.
 *
 * This component provides a complete overview of a person extracted from a historical record, including their
 * essential information, family relationships, events, additional facts, and tree attachment status. It allows
 * users to view the person's record, edit information through various cards, access change history, and delete
 * the person if needed.
 *
 * The component displays:
 * - Button to view the full record details in a new tab
 * - Tree attachment status
 * - Essential information card (names, gender, etc.)
 * - Family members card (relationships)
 * - Events card (birth, death, etc.)
 * - Additional facts card
 * - Change history
 * - Delete person functionality
 *
 * @param {Object} props - Component props
 * @param {Function} props.handleDeletePerson - Callback function to handle person deletion. Receives the person ID as a parameter.
 * @param {Function} props.onDirtyChange - Callback function that is called when the dirty state of any card changes.
 * Receives a boolean indicating whether there are unsaved changes.
 *
 * @returns {React.ReactElement} The rendered PersonOverviewV2 component
 *
 * @example
 * <PersonOverviewV2 handleDeletePerson={(personId) => deletePerson(personId)} onDirtyChange={(isDirty) => console.log(isDirty)} />
 */
const PersonOverviewV2 = ({ handleDeletePerson, onDirtyChange = () => {} }) => {
  const [t] = useTranslation()
  const { selectedPerson, selectedPersonName, currentImage } = useSelectedPerson()
  const recordOutlineEnabled = useRecordOutlineHighlightsEnabled()
  const { closeOverlay } = useOverlayContext()
  const [stepperState, stepperDispatch] = useReducer(stepperReducer, stepperEditViewInitialState)
  const zionSlsDispatch = useSlsDispatchContext()
  const { thumbnails } = useSharedBrowserStateContext()
  const r2State = useR2StateContext()
  const deletePersonOverlay = useOverlay()
  const splitScreenPartitionKey = r2State.splitScreen ? thumbnails?.[r2State?.referenceFocusedIndex]?.ark : null

  const { restrictedFields } = useCollectionContextProvider()
  const isRestrictedEssentialInfo =
    restrictedFields?.includes(FIELDS.NAME) &&
    restrictedFields?.includes(FIELDS.RACE) &&
    restrictedFields?.includes(FIELDS.SEX)
  const isRestrictedRelationshipField = restrictedFields?.includes(FIELDS.RELATIONSHIPS)
  const isRestrictedEventField = restrictedFields?.includes(FIELDS.EVENTS)
  const isRestrictedOtherField = restrictedFields?.includes(FIELDS.OTHER)
  const isRestrictedAddRemovePerson = restrictedFields?.includes(FIELDS.ADD_REMOVE_PERSON)

  const handleSubmit = async (currentStep) => {
    // Clear out the localizedNames value to trigger the hook to fetch it again.
    zionSlsDispatch({
      type: 'SET_PERSON_LOCALIZED_NAME',
      value: {
        personId: selectedPerson?.id,
        localizedName: '',
        partitionKey: currentImage?.partitionKey,
      },
    })
    stepperDispatch({ type: 'SET_EDIT_MODE', step: currentStep, value: false, returnToPreviousStep: true })

    // Reset dirty state after successful save to prevent unsaved changes dialog
    onDirtyChange(false)
  }

  // Set highlights for all person fields when all cards are in view mode (closed)
  const allCardsClosed = useMemo(
    () => Object.values(stepperState.editMode).every((isOpen) => !isOpen),
    [stepperState.editMode]
  )

  const fieldIds = useMemo(() => [selectedPerson?.id].filter(Boolean), [selectedPerson])

  // Every record's outline rect (not its fields), not just this person's record: record outlines are
  // always on, so opening a person leaves them as they were at image level. Passed through to
  // zion-sls's setDisplayHighlights below instead of writing to the record display API directly,
  // since that API is reactively overwritten by zion-sls's own sync effect whenever elementIds change.
  // Use the initialized state rather than re-reading the image: useImageHighlightInitialization
  // already merged in the neighbors' rects, which is the only way a record gets an outline on a page
  // where it is a dangling reference with no rects of its own.
  // useDisplayHighlights hands the same list to the main and the split screen calls, and zion-sls
  // keeps only each page's own rects by imageId, so the reference page's rects go in alongside.
  // Undefined (not []) with the flag off, so zion-sls keeps deriving the record highlights itself.
  const recordHighlightsForDisplay = useMemo(() => {
    if (!recordOutlineEnabled) return undefined
    const referenceRecordHighlights = r2State.splitScreen ? (r2State.referenceRecordHighlights ?? []) : []
    return [...(r2State.recordHighlights ?? []), ...referenceRecordHighlights]
  }, [recordOutlineEnabled, r2State.recordHighlights, r2State.referenceRecordHighlights, r2State.splitScreen])

  useDisplayHighlights({
    elementIds: fieldIds,
    recordHighlights: recordHighlightsForDisplay,
    enabled: allCardsClosed && !!selectedPerson && fieldIds.length > 0,
  })

  return (
    <FlowGrid columnCount={1} guttersY="xxs">
      <Button
        fullWidth
        color="gray"
        emphasis="medium"
        data-testid="person-overview-v2-view-record-button"
        to={`/ark:/61903/${selectedPerson?.id}`}
        target="_blank"
        external
        linkName="View Record Details"
        aria-label={t('person-overview-v2.view-record-details.aria-label', 'View Record Details (opens in a new tab)')}
      >
        {t('person-overview-v2.view-record-details', 'View Record Details')}
      </Button>
      <Paragraph size="sm">
        {t('person-overview-v2.edit-instruction', 'Click any edit button to fix the text or highlights.')}
      </Paragraph>
      <TreeAttachmentCard ark={`/ark:/61903/${selectedPerson?.id}`} />
      <EssentialInformationCard
        person={selectedPerson}
        reviewed
        onSave={() => handleSubmit(STEPS.ESSENTIAL_INFO)}
        editMode={stepperState.editMode[STEPS.ESSENTIAL_INFO]}
        setEditMode={(value) =>
          stepperDispatch({ type: 'SET_EDIT_MODE', step: STEPS.ESSENTIAL_INFO, value, returnToPreviousStep: true })
        }
        onDirtyChange={onDirtyChange}
        saveCardDataToSls
        splitScreenPartitionKey={splitScreenPartitionKey}
        disableEdit={isRestrictedEssentialInfo}
      />
      <RelationshipsCard
        person={selectedPerson}
        reviewed
        editMode={stepperState.editMode[STEPS.FAMILY_MEMBERS]}
        setEditMode={(value) =>
          stepperDispatch({ type: 'SET_EDIT_MODE', step: STEPS.FAMILY_MEMBERS, value, returnToPreviousStep: true })
        }
        onSave={() => handleSubmit(STEPS.FAMILY_MEMBERS)}
        onDirtyChange={onDirtyChange}
        saveCardDataToSls
        splitScreenPartitionKey={splitScreenPartitionKey}
        disableEdit={isRestrictedRelationshipField}
      />
      <EventsCard
        person={selectedPerson}
        reviewed
        editMode={stepperState.editMode[STEPS.EVENTS]}
        setEditMode={(value) =>
          stepperDispatch({ type: 'SET_EDIT_MODE', step: STEPS.EVENTS, value, returnToPreviousStep: true })
        }
        onSave={() => handleSubmit(STEPS.EVENTS)}
        onDirtyChange={onDirtyChange}
        saveCardDataToSls
        splitScreenPartitionKey={splitScreenPartitionKey}
        disableEdit={isRestrictedEventField}
      />
      <AdditionalFactsCard
        person={selectedPerson}
        reviewed
        editMode={stepperState.editMode[STEPS.ADDITIONAL_FACTS]}
        setEditMode={(value) =>
          stepperDispatch({ type: 'SET_EDIT_MODE', step: STEPS.ADDITIONAL_FACTS, value, returnToPreviousStep: true })
        }
        onSave={() => handleSubmit(STEPS.ADDITIONAL_FACTS)}
        onDirtyChange={onDirtyChange}
        saveCardDataToSls
        splitScreenPartitionKey={splitScreenPartitionKey}
        disableEdit={isRestrictedOtherField}
      />
      <ShowChangeHistory showIcon={false} />
      <Button
        dense
        emphasis="low"
        color="danger"
        data-testid="person-overview-v2-delete-person-button"
        disabled={isRestrictedAddRemovePerson}
        onClick={() => {
          deletePersonOverlay.handleClick()
        }}
      >
        {t('person-overview-v2.delete-person', 'Delete {selectedPersonName}', { selectedPersonName })}
      </Button>
      <DeleteOverlay
        overlay={deletePersonOverlay}
        headingText={t('remove-person.delete-individual-overlay.heading-text', `Delete {name}`, {
          name: selectedPersonName,
        })}
        handleDelete={() => {
          handleDeletePerson(selectedPerson?.id)
          closeOverlay('viewIndex')
        }}
      >
        <>
          {t(
            'remove-person.delete-individual-overlay.content-text',
            `Deleting {name} will remove this person and all of their content. Undo this action through this individual's Change History in the record.`,
            { name: selectedPersonName }
          )}
        </>
      </DeleteOverlay>
    </FlowGrid>
  )
}

export default PersonOverviewV2
